import { AxiosError } from 'axios';
import { ENVIRONMENT } from 'shared/config';

export const getCookieValue = (cookieName: string) => {
  const cookies = document.cookie.split(';');

  const targetCookie = cookies.find((cookie) => {
    const trimmedCookie = cookie.trim();
    return trimmedCookie.startsWith(`${cookieName}=`);
  });

  if (targetCookie) {
    return targetCookie.trim().substring(`${cookieName}=`.length);
  }

  return null; // If cookie is not found
};

export function setCookie(
  name: string,
  value: string,
  expiresTime?: Date | number | string
): void {
  let expires = '';
  if (expiresTime) {
    if (expiresTime instanceof Date) {
      expires = '; expires=' + expiresTime.toUTCString();
    }
    if (typeof expiresTime === 'string') {
      const date = new Date(expiresTime);
      expires = '; expires=' + date.toUTCString();
    }
    if (typeof expiresTime === 'number') {
      const date = new Date();
      date.setTime(date.getTime() + expiresTime * 1000);
      expires = '; expires=' + date.toUTCString();
    }
  }
  document.cookie =
    name +
    '=' +
    (value || '') +
    expires +
    '; path=/;' +
    `${ENVIRONMENT !== 'local' ? 'domain=.yepic.ai' : ''}`;
}

export function capitalize(str: string) {
  if (typeof str === 'string') {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  return str;
}

export const getErrorMessage = (
  err: unknown,
  fallbackMessage: string = '',
  showFallbackWError: boolean = false
) => {
  if (err instanceof AxiosError) {
    if (err.response && err.response.data) {
      const res = err.response.data;
      if ('message' in res) {
        return showFallbackWError
          ? fallbackMessage + ' Error: ' + res.message
          : res.message;
      }
      if ('title' in res) {
        return showFallbackWError
          ? fallbackMessage + ' Error: ' + res.title
          : res.message;
      }
    } else {
      return (
        fallbackMessage ||
        'An unexpected error occured. Please try again later.'
      );
    }
  }

  return (
    fallbackMessage || 'An unexpected error occured. Please try again later.'
  );
};

export const addZero = (n: number) => {
  if (n < 10) return `0${n}`;

  return n.toString();
};

export const getErrorMessageV2 = (
  error: unknown,
  errorsMap?: Record<string, string>
): string => {
  if (!(error instanceof Error)) {
    return 'Unknown error';
  }

  if (!(error instanceof AxiosError)) return error.message;

  const errorMessage = (() => {
    if (error.response?.status === 500)
      return 'An unexpected error occurred, please try again later';

    if (!error.response || !error.response.data) {
      return error.message;
    }

    const data = error.response.data;
    if (typeof data === 'string') {
      return data;
    }

    if (typeof data !== 'object' || !('detail' in data)) {
      return error.message;
    }

    if (typeof data.detail === 'string') {
      if (data.detail === 'Unauthorized') return null;
      return data.detail;
    }

    if (typeof data.detail === 'object' && data.detail) {
      if ('_detail' in data.detail && typeof data.detail._detail === 'string')
        return data.detail._detail;
      if ('reason' in data.detail && typeof data.detail.reason === 'string')
        return data.detail.reason;
      if ('code' in data.detail && typeof data.detail.code === 'string')
        return data.detail.code;
    }

    return error.message;
  })();

  if (errorsMap) {
    const message = errorsMap[errorMessage];
    if (typeof message === 'string') {
      return message;
    }
  }

  return errorMessage;
};
